"use client";

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../ui/components/Button';
import { IconButton } from '../../ui/components/IconButton';
import * as SubframeCore from '@subframe/core';
import { FeatherStar, FeatherCheck, FeatherShoppingCart, FeatherClock } from '@subframe/core';
import { Tooltip } from '../../ui/components/Tooltip';
import { formatPrice } from '../../utils/formatters';
import { is3DModel } from '../../utils/media';
import { isDealsBrokeredOffer } from '../../lib/marketplace-constants';
import { RarityRank } from './RarityRank';
import { formatPoints } from './PointsBadge';

/**
 * Base NFT fields required for the card display
 */
interface NFTBaseFields {
  // Token ID - various field names used across the app
  nft_token_id?: string;
  nftTokenId?: string;
  token_id?: string;
  id?: string;

  // Name
  name: string | null;

  // Image - various field names
  thumbnail_url?: string | null;
  media_url?: string | null;
  image_url?: string | null;
  image?: string | null;

  // Media type (e.g. 'image/gif', 'model/gltf-binary')
  media_type?: string | null;

  // Optional marketplace fields
  rarity_rank?: number | null;
  min_sell_price?: number | null;
  last_sale_price?: number | null;
  is_promoted?: boolean;
  collection_points?: number | null;

  // Active offers (sorted by price, lowest first)
  active_offers?: Array<{ offer_id: string; destination?: string | null; [key: string]: any }>;

  // Active auction summary (set when this NFT has an in-progress auction).
  // Drops fields are strings (BIGINT serialization).
  auction?: {
    id: string;
    expires_at: string;
    reserve_price_drops: string;
    min_bid_drops: string;
    top_bid_drops: string | null;
  } | null;
}

interface NFTCardProps<T extends NFTBaseFields> {
  nft: T;
  showBuyButton?: boolean;
  onBuyClick?: (nft: T, event: React.MouseEvent) => void;
  isAuthenticated?: boolean;
  totalSupply?: number;
  /** Show selection highlight (for sweep mode) */
  isSelected?: boolean;
  /** Add to cart handler */
  onAddToCart?: (nft: T, event: React.MouseEvent) => void;
  /** Whether the NFT is already in the cart */
  isInCart?: boolean;
  /** Whether this NFT is eligible for an active sweepstakes contest */
  contestEligible?: boolean;
  /** Use original image URL instead of cached CloudFront URL (for dynamic NFT collections) */
  useOriginalMedia?: boolean;
  /** Whether to show the "(X Pts/XRP)" suffix next to the points count. Hide when the
   *  collection's points are uniform — the ratio is the same for every listed NFT and
   *  just adds visual noise. */
  showPointsPerXrp?: boolean;
}

/**
 * Get the NFT token ID from various possible field names
 */
const getTokenId = (nft: NFTBaseFields): string => {
  return nft.nft_token_id || nft.nftTokenId || nft.token_id || nft.id || '';
};

/** Compact auction countdown — "2d 4h", "1h 23m", "12m 5s", "8s", or "Ending". */
function formatAuctionRemaining(remainingMs: number): string {
  if (remainingMs <= 0) return 'Ending';
  const total = Math.floor(remainingMs / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const AuctionCardCountdown: React.FC<{ expiresAt: string }> = ({ expiresAt }) => {
  const targetMs = new Date(expiresAt).getTime();
  const [remaining, setRemaining] = useState(() => Math.max(0, targetMs - Date.now()));

  useEffect(() => {
    const id = window.setInterval(() => {
      setRemaining(Math.max(0, targetMs - Date.now()));
    }, 1000);
    return () => window.clearInterval(id);
  }, [targetMs]);

  return <>{formatAuctionRemaining(remaining)}</>;
};

/**
 * Get the best available image URL, handling IPFS URLs
 */
const getImageUrl = (nft: NFTBaseFields, useOriginalMedia?: boolean): string => {
  // If collection uses original media (dynamic NFTs), prefer original URL
  if (useOriginalMedia && (nft.image_url || nft.image)) {
    const url = nft.image_url || nft.image || '';
    if (url.startsWith('ipfs://')) {
      return url.replace('ipfs://', 'https://ipfs.io/ipfs/');
    }
    return url;
  }

  // Skip media_url if it's a relative S3 key (not a full URL)
  const validMediaUrl = nft.media_url && (nft.media_url.startsWith('http://') || nft.media_url.startsWith('https://'))
    ? nft.media_url : null;

  // For GIFs, prefer the original media_url to preserve animation
  const isGif = nft.media_type === 'image/gif';
  const url = isGif
    ? (validMediaUrl || nft.thumbnail_url || nft.image_url || nft.image || '')
    : (nft.thumbnail_url || validMediaUrl || nft.image_url || nft.image || '');

  // Convert IPFS URLs to gateway URLs
  if (url.startsWith('ipfs://')) {
    return url.replace('ipfs://', 'https://ipfs.io/ipfs/');
  }

  return url;
};

/**
 * NFTCard - Unified card component for displaying NFTs in grids
 *
 * Used by:
 * - Collection page (NFTs tab) - with Buy Now button
 * - Wallet page (collection view) - without Buy Now button
 *
 * Generic type T preserves the original NFT type for the onBuyClick callback
 */
export function NFTCard<T extends NFTBaseFields>({
  nft,
  showBuyButton = false,
  onBuyClick,
  isAuthenticated = false,
  totalSupply,
  isSelected = false,
  onAddToCart,
  isInCart = false,
  contestEligible = false,
  useOriginalMedia = false,
  showPointsPerXrp = true,
}: NFTCardProps<T>) {
  const tokenId = getTokenId(nft);
  const imageUrl = getImageUrl(nft, useOriginalMedia);
  const is3D = is3DModel(nft);
  const price = nft.min_sell_price || nft.last_sale_price;
  const hasPrice = price !== undefined && price !== null;
  const isFeatured = nft.is_promoted === true;
  const isDealsListed = nft.min_sell_price != null && isDealsBrokeredOffer(nft.active_offers?.[0]?.destination);

  // Auction state — surfaces only when there's no buyable listing (auction
  // sell offers are excluded from min_sell_price by design). Display price is
  // the top bid if any bids landed, otherwise the min bid as the floor. The
  // reserve is intentionally omitted from the card (lives on the detail page)
  // so heights stay aligned with listed/unlisted cards in the same grid row.
  const auction = nft.auction;
  const auctionTopBidXrp = auction?.top_bid_drops
    ? Number(auction.top_bid_drops) / 1_000_000
    : null;
  const auctionMinBidXrp = auction
    ? Number(auction.min_bid_drops) / 1_000_000
    : null;
  const auctionDisplayPriceXrp = auctionTopBidXrp ?? auctionMinBidXrp;

  // Collection_points comes from Postgres DECIMAL — may be a string.
  const pointsNumber = (() => {
    if (nft.collection_points == null) return null;
    const n = typeof nft.collection_points === 'number'
      ? nft.collection_points
      : parseFloat(String(nft.collection_points));
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  // Pts/XRP only when listed for sale; use min_sell_price (not last_sale_price).
  const pointsPerXrp = (() => {
    if (pointsNumber == null) return null;
    if (nft.min_sell_price == null || nft.min_sell_price <= 0) return null;
    return pointsNumber / nft.min_sell_price;
  })();

  // If the full NFT name parses as a positive integer, prefix with "#" (e.g., "995" → "#995").
  const displayName = (() => {
    const raw = nft.name?.trim();
    if (!raw) return `NFT #${tokenId.slice(0, 8)}...`;
    return /^\d+$/.test(raw) ? `#${raw}` : raw;
  })();

  const handleBuyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (onBuyClick) {
      onBuyClick(nft, event);
    }
  };

  const handleAddToCart = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (onAddToCart) {
      onAddToCart(nft, event);
    }
  };

  // Determine border/ring styling based on state
  const getBorderClasses = () => {
    if (isSelected) {
      return 'border-brand-600 ring-2 ring-brand-600 ring-offset-2 ring-offset-default-background';
    }
    if (isFeatured) {
      return 'border-amber-400 ring-2 ring-amber-200';
    }
    if (isDealsListed) {
      return 'border-transparent deals-border-shimmer';
    }
    return 'border-neutral-border';
  };

  return (
    <Link
      to={`/nfts/${tokenId}`}
      className={`flex grow shrink-0 basis-0 flex-col items-start rounded-md border border-solid h-full ${getBorderClasses()} bg-default-background shadow-sm hover:shadow-md transition-all ${isDealsListed ? '' : 'overflow-hidden'}`}
    >
      {/* Image Container */}
      <div className={`relative w-full isolate ${isDealsListed ? 'overflow-hidden rounded-t-md' : ''}`}>
        {is3D && nft.thumbnail_url ? (
          <img
            className="w-full flex-none aspect-square object-cover"
            src={nft.thumbnail_url}
            alt={nft.name || '3D NFT'}
          />
        ) : is3D ? (
          <div className="w-full flex-none aspect-square bg-gradient-to-br from-neutral-100 to-neutral-200 flex flex-col items-center justify-center gap-1">
            <span className="text-3xl">🧊</span>
            <span className="text-xs font-medium text-neutral-500">3D Model</span>
          </div>
        ) : imageUrl ? (
          <img
            className="w-full flex-none aspect-square object-cover"
            src={imageUrl}
            alt={nft.name || 'NFT'}
            onError={(e) => {
              // Hide broken image and show placeholder
              e.currentTarget.style.display = 'none';
              const placeholder = e.currentTarget.nextElementSibling;
              if (placeholder) {
                placeholder.classList.remove('hidden');
              }
            }}
          />
        ) : null}
        {/* 3D badge */}
        {is3D && (
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1">
            <span style={{ fontSize: '10px' }}>🧊</span> 3D
          </div>
        )}
        {/* Fallback placeholder - shown when no image or image fails to load */}
        <div
          className={`w-full flex-none aspect-square bg-neutral-100 flex items-center justify-center ${imageUrl || is3D ? 'hidden' : ''}`}
        >
          <span className="text-heading-2 font-heading-2 text-neutral-400">NFT</span>
        </div>

        {/* Selection Checkmark (for sweep mode) */}
        {isSelected && (
          <div className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-brand-600 shadow-md z-10">
            <FeatherCheck className="h-4 w-4 text-white" />
          </div>
        )}

        {/* Featured Badge */}
        {isFeatured && (
          <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-2 py-1 shadow-md">
            <FeatherStar className="h-3 w-3 text-white fill-white" />
            <span className="text-xs font-semibold text-white">Featured</span>
          </div>
        )}

        {/* Contest Eligible Badge */}
        {contestEligible && !isFeatured && (
          <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-brand-600 px-2 py-1 shadow-md">
            <span className="text-xs font-semibold text-white">Contest Eligible</span>
          </div>
        )}

        {/* Auction Badge — shown when this NFT has an active auction. Sits in
            the bottom-left so it doesn't collide with Featured / Contest /
            selection badges, and is visible without making the card taller. */}
        {auction && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-brand-600 px-2 py-1 shadow-md">
            <FeatherClock className="h-3 w-3 text-white" />
            <span className="text-xs font-semibold text-white">Auction</span>
          </div>
        )}

      </div>

      {/* Content */}
      <div className="flex w-full flex-col items-start gap-3 px-4 py-4 flex-grow">
        {/* Name, Rank, and Points */}
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-body-bold font-body-bold text-default-font truncate w-full">
            {displayName}
          </span>
          {nft.rarity_rank && totalSupply && (
            <RarityRank
              rank={nft.rarity_rank}
              totalSupply={totalSupply}
              className="text-caption font-caption"
            />
          )}
          {pointsNumber != null && (
            <span className="text-caption font-caption text-subtext-color">
              {formatPoints(pointsNumber)} {pointsNumber === 1 ? 'point' : 'points'}
              {showPointsPerXrp && pointsPerXrp != null && (
                <> ({formatPoints(pointsPerXrp)} Pts/XRP)</>
              )}
            </span>
          )}
        </div>

        {/* Price and Buy Button - mt-auto pushes to bottom for consistent alignment */}
        {hasPrice ? (
          <div className="flex w-full flex-col mobile:gap-2 gap-2 mt-auto">
            <div className="flex w-full items-center justify-between">
              <span className={`text-body-bold font-body-bold text-brand-700 flex items-center gap-1${isDealsListed ? ' deals-shimmer' : ''}`}>
                {formatPrice(price)}
                {isDealsListed && (
                  <SubframeCore.Tooltip.Provider delayDuration={0}>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild>
                        <span className="inline-flex cursor-help">
                          <svg className="h-3.5 w-3.5 text-brand-600" viewBox="0 0 20 20" fill="currentColor" aria-label="Listed on xrp.deals">
                            <path fillRule="evenodd" d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.883l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content side="top" align="center" sideOffset={4} asChild>
                          <Tooltip>Listed on xrp.deals</Tooltip>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                )}
              </span>
              <div className="flex items-center gap-1">
                {/* Only show Buy Now button for authenticated users */}
                {showBuyButton && onBuyClick && isAuthenticated && (
                  <Button
                    className="hidden mobile:hidden sm:block"
                    size="small"
                    onClick={handleBuyClick}
                  >
                    Buy Now
                  </Button>
                )}
                {/* Cart button - desktop */}
                {onAddToCart && isAuthenticated && (
                  <IconButton
                    className="hidden mobile:hidden sm:flex"
                    size="small"
                    variant={isInCart ? 'brand-secondary' : 'neutral-tertiary'}
                    icon={isInCart ? <FeatherCheck className="h-3.5 w-3.5" /> : <FeatherShoppingCart className="h-3.5 w-3.5" />}
                    disabled={isInCart}
                    onClick={handleAddToCart}
                  />
                )}
              </div>
            </div>
            {/* Mobile Buy Now + Cart buttons - only for authenticated users */}
            {showBuyButton && onBuyClick && isAuthenticated && (
              <div className="flex sm:hidden w-full gap-2">
                <Button
                  className="flex-1"
                  size="small"
                  onClick={handleBuyClick}
                >
                  Buy Now
                </Button>
                {onAddToCart && (
                  <IconButton
                    size="small"
                    variant={isInCart ? 'brand-secondary' : 'neutral-tertiary'}
                    icon={isInCart ? <FeatherCheck className="h-3.5 w-3.5" /> : <FeatherShoppingCart className="h-3.5 w-3.5" />}
                    disabled={isInCart}
                    onClick={handleAddToCart}
                  />
                )}
              </div>
            )}
          </div>
        ) : auction ? (
          // Match the listed-card layout: one bottom row with price on the
          // left + countdown chip on the right. Keeps card heights uniform
          // across the grid (auctions, listed, and unlisted all align).
          <div className="flex w-full items-center justify-between gap-2 mt-auto">
            <span className="text-body-bold font-body-bold text-brand-700">
              {auctionDisplayPriceXrp != null ? formatPrice(auctionDisplayPriceXrp) : 'Live'}
            </span>
            <span className="flex items-center gap-1 text-caption font-caption text-subtext-color whitespace-nowrap">
              <FeatherClock className="h-3 w-3" />
              <AuctionCardCountdown expiresAt={auction.expires_at} />
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-none items-center gap-2 mt-auto">
            <span className="text-caption font-caption text-subtext-color">
              Not Listed
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}
